import { useCallback, useEffect, useState, useMemo } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

// Components
import Select from '../../../../components/atoms/select';
import Button from '../../../../components/atoms/button';
import Table, { TableColumn } from '../../../../components/organisms/table';
import Text from '../../../../components/atoms/text';
import Pagination, {
	PaginationProps,
} from '../../../../components/organisms/pagination';
import { NotificationToast } from '../../../../components/organisms/notification-toast';

// Services
import YardService, { IYard } from '../../../../services/Yard.service';
import ShipService, { IShip } from '../../../../services/Ship.service';

// Utils & Types
import { EditableChangeEvent } from '../../../../utils/types/editable';
import { dateObjToStr } from '../../../../utils/date';
import { HomeLoaderData } from '../../../../types/homeLoaderData';
import ContainerService, {
	IContainer,
} from '../../../../services/Container.service';
import Input from '../../../../components/atoms/input';

// ================ Constants & Types ================
interface TravelForm {
	yard: Partial<IYard>;
	ship: Partial<IShip>;
	travelCode: string;
}

const DEFAULT_FORM: TravelForm = {
	yard: {},
	ship: {},
	travelCode: '',
};

const DEFAULT_PAGINATION: PaginationProps = {
	pageCurrent: 1,
	pageSize: 10,
	totalItems: 0,
	totalPages: 1,
};

interface ContainerR extends IContainer {
	checked: boolean;
}

// ================ Main Component ================
const AuditoryReturn = () => {
	const { t } = useTranslation();
	const { accountId } = useRouteLoaderData('home') as HomeLoaderData;

	// ================ State ================
	const [yards, setYards] = useState<IYard[]>([]);
	const [ships, setShips] = useState<IShip[]>([]);
	const [form, setForm] = useState<TravelForm>(DEFAULT_FORM);
	const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
	const [containers, setContainers] = useState<ContainerR[]>([]);
	const [pagination, setPagination] =
		useState<PaginationProps>(DEFAULT_PAGINATION);

	// ================ Table Columns ================
	const columns: TableColumn<ContainerR>[] = useMemo(
		() => [
			{
				key: 'actions',
				label: t('misc.actions'),
				children: (row) => (
					<input
						type="checkbox"
						checked={row.checked}
						onClick={() => handleCheck(row)}
					/>
				),
			},
			{ key: 'label', label: t('port_management.container') },
			{
				key: 'dateArrive',
				label: t('date.title'),
				children: (row) => (
					<Text className="text-xs">
						{dateObjToStr(row.dateArrive)}
					</Text>
				),
			},
		],
		[t, containers]
	);

	const fetchYards = useCallback(async () => {
		if (!accountId) return;

		try {
			const response = await YardService.getAll({
				isActive: 1,
				idClient: Number(accountId),
			});

			if (response?.data) {
				setYards(response.data);
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_yards'));
		}
	}, [accountId, t]);

	const fetchShips = useCallback(async () => {
		if (!accountId) return;

		try {
			const response = await ShipService.getAll({
				isActive: 1,
				idClient: Number(accountId),
			});

			if (response?.data) {
				setShips(response.data);
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_ships'));
		}
	}, [accountId, t]);

	const fetchContainers = useCallback(async () => {
		if (!form.yard.id && !form.ship.id) return;
		try {
			const response = await ContainerService.getReturnContainers(
				{
					pageCurrent: pagination.pageCurrent,
					pageSize: pagination.pageSize,
					idClient: Number(accountId),
				},
				form.yard?.id,
				form.ship?.id,
				form.travelCode !== '' ? form.travelCode : undefined
			);

			if (response) {
				setContainers(
					response.data.map((container) => ({
						...container,
						checked: false,
					}))
				);
				setPagination((prev) => ({
					...prev,
					totalItems: response.totalItems,
					totalPages: response.totalPages,
				}));
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_movements'));
		}
	}, [pagination.pageCurrent, pagination.pageSize, searchTrigger]);

	// ================ Effects ================
	useEffect(() => {
		fetchYards();
	}, [fetchYards]);

	useEffect(() => {
		fetchShips();
	}, [fetchShips]);

	useEffect(() => {
		fetchContainers();
	}, [fetchContainers]);

	// ================ Handlers ================
	const handleCheck = (container: ContainerR) => {
		const index = containers.findIndex((i) => i.id == container.id);
		const newData = [...containers];
		newData[index].checked = !newData[index].checked;
		setContainers(newData);
	};

	const handleTravelCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setForm((prev) => ({ ...prev, travelCode: e.target.value }));
	};

	const handleYardChange = useCallback(
		(e: EditableChangeEvent) => {
			const yard = yards.find((y) => y.name === e.target.value);
			if (!yard) return;

			setForm((prev) => ({ ...prev, yard }));
		},
		[yards]
	);

	const handleShipChange = useCallback(
		(e: EditableChangeEvent) => {
			const ship = ships.find((s) => s.name === e.target.value);
			if (!ship) return;

			setForm((prev) => ({ ...prev, ship }));
		},
		[ships]
	);

	const handleSearch = useCallback(() => {
		setSearchTrigger((prev) => !prev);
	}, []);

	const handleResetFilters = useCallback(() => {
		// Reset form to default values
		setForm(DEFAULT_FORM);
		// Trigger search to show all results
		setSearchTrigger((prev) => !prev);
		// Reset to first page
		setPagination((prev) => ({ ...prev, pageCurrent: 1 }));
	}, []);

	const handlePaginationUpdate = useCallback(
		(pageSize: number, pageCurrent: number) => {
			setPagination((prev) => ({ ...prev, pageSize, pageCurrent }));
		},
		[]
	);

	const handleSave = async () => {
		const ids = [];
		for (let container of containers) {
			if (container.checked) ids.push(container.id);
		}
		if (ids.length == 0) {
			NotificationToast.Warn(t('port_management.return.not_selected'));
			return;
		}
		const response = await ContainerService.activate(ids);
		if (response) {
			NotificationToast.Success(t('alert.success.generic'));
			handleSearch();
		}
	};

	// ================ Render Helpers ================
	const renderFilters = () => (
		<div className="flex gap-x-8 gap-y-4 flex-wrap items-end">
			<Select
				boxClassName="min-w-60"
				name="yard"
				value={form.yard.name || ''}
				onChange={handleYardChange}
				label={t('port_management.yard')}
				placeholder={t('port_management.yard')}
				options={yards.map((y) => y.name)}
				disabled={!accountId}
			/>

			<Select
				boxClassName="min-w-60"
				name="ship"
				value={form.ship.name || ''}
				onChange={handleShipChange}
				label={t('port_management.ship')}
				placeholder={t('port_management.ship')}
				options={ships.map((s) => s.name)}
				disabled={!accountId}
			/>

			<Input
				className="min-w-60"
				name="travelCode"
				value={form.travelCode}
				onChange={handleTravelCodeChange}
				label={t('movement.travel_code')}
				placeholder={t('movement.travel_code')}
			/>

			<div className="flex gap-2">
				<Button onClick={handleSearch}>{t('misc.search')}</Button>

				<Button theme="light" onClick={handleResetFilters}>
					{t('misc.reset')}
				</Button>
			</div>
		</div>
	);

	const renderTable = () => (
		<div className="overflow-x-auto w-full flex flex-col gap-8">
			<Table
				data={containers}
				columns={columns}
				className={classNames('grid-cols-[auto_1fr_1fr]')}
			/>
		</div>
	);

	// ================ Render ================
	return (
		<div className="flex flex-col gap-4 px-8 py-4">
			{renderFilters()}
			{renderTable()}
			<Button className="w-fit self-end" onClick={handleSave}>
				{t('misc.save')}
			</Button>
			<Pagination {...pagination} onUpdate={handlePaginationUpdate} />
		</div>
	);
};

export default AuditoryReturn;
